import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Normaliza el `detail` de un error de la API a un string seguro para render.
// FastAPI/Pydantic devuelve `detail` como string (errores propios) o como
// array de objetos {loc, msg, type} ante un 422. Renderizar ese array/objeto
// directo como hijo de React rompe la pantalla ("Objects are not valid as a
// React child"). Esto siempre retorna texto.
export function formatApiError(detail, fallback = 'Algo salió mal. Intentá nuevamente.') {
  if (detail == null) return fallback;
  if (typeof detail === 'string') return detail || fallback;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' · ') || fallback;
  if (typeof detail === 'object') return (typeof detail.msg === 'string' && detail.msg) || JSON.stringify(detail);
  return String(detail);
}

// Sanitiza HTML (p.ej. la respuesta del Asistente IA) con whitelist de tags y
// atributos, usando el parser del navegador. Quita scripts, handlers on*, estilos
// y URLs peligrosas. Pensado para HTML generado por nuestra IA en un módulo
// autenticado; no reemplaza a DOMPurify para contenido público arbitrario.
const _TAGS_OK = new Set([
  'p', 'br', 'hr', 'strong', 'b', 'em', 'i', 'u', 's', 'small', 'mark',
  'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'blockquote', 'code', 'pre', 'span', 'div', 'a',
  'table', 'thead', 'tbody', 'tr', 'td', 'th',
]);
const _ATTRS_OK = { a: ['href', 'target', 'rel'] };

// Conversor Markdown → HTML mínimo para las respuestas del Asistente IA
// (encabezados #, negrita **, cursiva _ /*, código `, listas - / 1., hr ---/—,
// links [t](url)). Escapa el texto antes de inyectar nuestras etiquetas.
function _escHtml(s) {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
function _inline(s) {
  let t = s;
  t = t.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2">$1</a>');
  t = t.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  t = t.replace(/(^|[\s(])\*(?!\s)(.+?)\*(?=[\s).,;:!?]|$)/g, '$1<em>$2</em>');
  t = t.replace(/(^|[\s(])_(?!\s)(.+?)_(?=[\s).,;:!?]|$)/g, '$1<em>$2</em>');
  t = t.replace(/`([^`]+?)`/g, '<code>$1</code>');
  return t;
}
export function markdownToHtml(md) {
  if (!md || typeof md !== 'string') return '';
  const lineas = _escHtml(md).replace(/\r\n/g, '\n').split('\n');
  const out = [];
  let lista = null; // 'ul' | 'ol'
  const cerrarLista = () => { if (lista) { out.push(`</${lista}>`); lista = null; } };
  for (const raw of lineas) {
    const linea = raw.replace(/\s+$/, '');
    if (!linea.trim()) { cerrarLista(); continue; }
    if (/^\s*(([-*_])\s*){3,}$/.test(linea) || /^\s*—{1,}\s*$/.test(linea)) { cerrarLista(); out.push('<hr>'); continue; }
    const h = linea.match(/^(#{1,4})\s+(.*)$/);
    if (h) { cerrarLista(); const lvl = h[1].length; out.push(`<h${lvl}>${_inline(h[2])}</h${lvl}>`); continue; }
    const ul = linea.match(/^\s*[-*]\s+(.*)$/);
    if (ul) { if (lista !== 'ul') { cerrarLista(); out.push('<ul>'); lista = 'ul'; } out.push(`<li>${_inline(ul[1])}</li>`); continue; }
    const ol = linea.match(/^\s*\d+[.)]\s+(.*)$/);
    if (ol) { if (lista !== 'ol') { cerrarLista(); out.push('<ol>'); lista = 'ol'; } out.push(`<li>${_inline(ol[1])}</li>`); continue; }
    cerrarLista();
    out.push(`<p>${_inline(linea)}</p>`);
  }
  cerrarLista();
  return out.join('\n');
}

// True si el texto ya parece HTML (la IA a veces responde en HTML en vez de Markdown).
export function pareceHtml(s) {
  return typeof s === 'string' && /<\/?(p|ul|ol|li|h[1-6]|strong|em|br|hr|blockquote|table|div)\b/i.test(s);
}

export function sanitizeHtmlBasico(html) {
  if (!html || typeof html !== 'string') return '';
  let doc;
  try { doc = new DOMParser().parseFromString(html, 'text/html'); }
  catch { return ''; }
  const limpiar = (node) => {
    Array.from(node.children).forEach((el) => {
      const tag = el.tagName.toLowerCase();
      if (!_TAGS_OK.has(tag)) { el.remove(); return; } // script/style/iframe/etc fuera
      Array.from(el.attributes).forEach((attr) => {
        const name = attr.name.toLowerCase();
        const permitidas = _ATTRS_OK[tag] || [];
        if (name.startsWith('on') || !permitidas.includes(name)) el.removeAttribute(attr.name);
      });
      if (tag === 'a') {
        const href = el.getAttribute('href') || '';
        if (/^\s*(javascript|data|vbscript):/i.test(href)) el.removeAttribute('href');
        el.setAttribute('target', '_blank');
        el.setAttribute('rel', 'noopener noreferrer');
      }
      limpiar(el);
    });
  };
  limpiar(doc.body);
  return doc.body.innerHTML;
}
